import { useTranslation } from 'react-i18next';
import { MessageSquareX, CreditCard, CloudOff } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Reveal, RevealGroup, RevealItem } from './Reveal';

interface PainPointItem {
  readonly key: string;
  readonly Icon: LucideIcon;
}

const PAIN_POINTS: readonly PainPointItem[] = [
  { key: 'item1', Icon: MessageSquareX },
  { key: 'item2', Icon: CreditCard },
  { key: 'item3', Icon: CloudOff },
] as const;

export default function PainPoints() {
  const { t } = useTranslation();

  return (
    <section
      id="pain-points"
      className="py-24 md:py-32 bg-[#f0eee6] dark:bg-[#1a1918]"
      aria-labelledby="pain-points-heading"
    >
      <div className="mx-auto max-w-6xl px-6">
        {/* Section header */}
        <Reveal className="mb-14 text-center">
          <span className="eyebrow inline-flex items-center gap-2 rounded-full border border-[#1414131a] dark:border-[#faf9f51a] px-4 py-1.5 text-[#6b6a68] dark:text-[#9a9893]">
            <span className="h-1.5 w-1.5 rounded-full bg-[#ae5630]" />
            {t('painPoints.eyebrow', '痛点洞察')}
          </span>
          <h2
            id="pain-points-heading"
            className="font-display display-tight mt-5 text-3xl font-medium leading-[1.1] text-[#141413] dark:text-[#faf9f5] md:text-5xl"
          >
            {t('painPoints.title')}
          </h2>
        </Reveal>

        {/* 非对称 bento：大卡 + 小卡错落 */}
        <RevealGroup className="grid grid-cols-1 gap-4 md:grid-cols-12">
          {/* 主卡（item1）— 跨 7 列，双层 bezel */}
          <RevealItem className="md:col-span-7">
            <div className="h-full rounded-[1.5rem] border border-[#1414131a] bg-[#1414130a] p-1.5 shadow-warm dark:border-[#faf9f51a] dark:bg-[#faf9f50a]">
              <div className="group flex h-full flex-col rounded-[calc(1.5rem-0.375rem)] border border-[#1414130d] bg-[#fffefb] px-8 py-8 shadow-inner-glow transition-all duration-300 ease-spring dark:border-[#faf9f50d] dark:bg-[#1a1918]">
                <div className="mb-6 flex h-14 w-14 items-center justify-center rounded-2xl bg-[#ae5630]/10 text-[#ae5630] transition-all duration-300 ease-spring group-hover:bg-[#ae5630]/20 dark:bg-[#ae5630]/15 dark:group-hover:bg-[#ae5630]/25">
                  <MessageSquareX size={26} aria-hidden="true" />
                </div>
                <h3 className="font-display display-tight text-2xl font-medium leading-[1.15] text-[#141413] dark:text-[#faf9f5] md:text-3xl">
                  {t('painPoints.item1.title')}
                </h3>
                <p className="mt-4 max-w-xs text-base leading-relaxed text-[#6b6a68] dark:text-[#9a9893]">
                  {t('painPoints.item1.desc')}
                </p>
                {/* 装饰线条 */}
                <div className="mt-8 h-px w-full bg-gradient-to-r from-[#ae5630]/30 via-[#ae5630]/10 to-transparent" />
              </div>
            </div>
          </RevealItem>

          {/* 右侧列：item2 + item3 上下堆叠，跨 5 列 */}
          <div className="flex flex-col gap-4 md:col-span-5">
            {PAIN_POINTS.slice(1).map(({ key, Icon }) => (
              <RevealItem key={key}>
                <div className="group rounded-2xl border border-[#1414131a] bg-[#fffefb] p-6 shadow-warm transition-all duration-300 ease-spring hover:border-[#ae5630]/30 dark:border-[#faf9f51a] dark:bg-[#1a1918] dark:hover:border-[#ae5630]/30">
                  <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-[#ae5630]/10 text-[#ae5630] transition-all duration-300 ease-spring group-hover:bg-[#ae5630]/20 dark:bg-[#ae5630]/15 dark:group-hover:bg-[#ae5630]/25">
                    <Icon size={20} aria-hidden="true" />
                  </div>
                  <h3 className="font-display display-tight text-lg font-medium text-[#141413] dark:text-[#faf9f5]">
                    {t(`painPoints.${key}.title`)}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-[#6b6a68] dark:text-[#9a9893]">
                    {t(`painPoints.${key}.desc`)}
                  </p>
                </div>
              </RevealItem>
            ))}
          </div>
        </RevealGroup>
      </div>
    </section>
  );
}
